// SubsidiaryModal.jsx
import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import "./Modal.css";
import { FiImage, FiUpload, FiX } from "react-icons/fi";
import axiosInstance from "../axiosinstance";
import {
  validateGST,
  validateMobile,
  validateIFSC,
  validateAccountNumber,
} from "../helperFunctions";
import { Snackbar, Alert as MuiAlert } from "@mui/material";
const INITIAL_FORM = {
  NameofCompany: "",
  NameofOwner: "",
  Address: "",
  MobileNo: "",
  Email: "",
  GstNO: "",
  InvoicingLimit: "",
  TagName: "",
  AccountNumber: "",
  AccountHolder: "",
  IFSCCode: "",
  Branch: "",
  PanNumber: "",
  VirtualPayementAddress: "",
};

export default function SubsidiaryModal({ isOpen, onClose, onSuccess }) {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [isGstYes, setIsGstYes] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [qrCodeFile, setQrCodeFile] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const firstInputRef = useRef(null);
  const paymentQrInputRef = useRef(null);

  // Focus first input on open
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => firstInputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Lock body scroll when open
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const resetForm = () => {
    setFormData(INITIAL_FORM);
    setIsGstYes(false);
    setErrors({});
    setLogo(null);
    setLogoPreview(null);
    setQrCodeFile(null);
    if (paymentQrInputRef.current) paymentQrInputRef.current.value = "";
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updated =
      name === "PanNumber" || name === "IFSCCode" ? value.toUpperCase() : value;
    setFormData((prev) => ({ ...prev, [name]: updated }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleMobileChange = (value) => {
    const next = value.replace(/\D/g, "").slice(0, 10);
    setFormData((prev) => ({ ...prev, MobileNo: next }));
    setErrors((prev) => ({ ...prev, MobileNo: validateMobile(next) || "" }));
  };

  const handleGstChange = (e) => {
    const isYes = e.target.value === "yes";
    setIsGstYes(isYes);
    setFormData((prev) => ({ ...prev, GstNO: "" }));
    setErrors((prev) => ({ ...prev, GstNO: "" }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    setLogo(file || null);
    setLogoPreview(file ? URL.createObjectURL(file) : null);
    setErrors((prev) => ({ ...prev, logo: "" }));
  };

  const handlePaymentQrChange = (e) => {
    const file = e.target.files?.[0] || null;
    if (!file) {
      setQrCodeFile(null);
      return;
    }
    if (!file.type?.startsWith("image/")) {
      setErrors((prev) => ({ ...prev, qrCodeFile: "Only image files are allowed." }));
      if (paymentQrInputRef.current) paymentQrInputRef.current.value = "";
      return;
    }
    setQrCodeFile(file);
    setErrors((prev) => ({ ...prev, qrCodeFile: "" }));
  };

  const handleClearQr = () => {
    setQrCodeFile(null);
    if (paymentQrInputRef.current) paymentQrInputRef.current.value = "";
  };

  // --- Validation ---
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
  const vpaRegex = /^[a-zA-Z0-9.\-_]+@[a-zA-Z0-9]+$/;

  const fieldRules = {
    NameofCompany: { required: true, max: 100, label: "Company Name" },
    NameofOwner: { required: true, max: 50, label: "Proprietor Name" },
    Address: { required: true, max: 250, label: "Address" },
    MobileNo: { required: true, custom: validateMobile, label: "Mobile Number" },
    Email: { required: true, pattern: emailRegex, label: "Email" },
    GstNO: { required: isGstYes, label: "GST Number", custom: isGstYes ? validateGST : null },
    InvoicingLimit: { required: true, numeric: true, label: "Invoicing Limit" },
    TagName: { required: true, max: 50, label: "Tag Name" },
    AccountNumber: { required: true, label: "Account Number", custom: validateAccountNumber },
    AccountHolder: { required: true, label: "Account Holder" },
    IFSCCode: { required: true, label: "IFSC Code", custom: validateIFSC },
    Branch: { required: false },
    PanNumber: { required: true, pattern: panRegex, label: "PAN Number" },
    VirtualPayementAddress: {
      required: true,
      pattern: vpaRegex,
      max: 100,
      label: "Virtual Payment Address",
    },
  };

  const validateForm = () => {
    const newErrors = {};

    Object.keys(fieldRules).forEach((field) => {
      const value = formData[field]?.toString().trim();
      const rules = fieldRules[field];
      const label = rules.label || field;

      if (rules.required && !value) {
        newErrors[field] = `${label} is required`;
        return;
      }
      if (value && rules.max && value.length > rules.max) {
        newErrors[field] = `${label} cannot exceed ${rules.max} characters`;
        return;
      }
      if (value && rules.pattern && !rules.pattern.test(value)) {
        newErrors[field] = `Invalid ${label}`;
        return;
      }
      if (value && rules.numeric && isNaN(value)) {
        newErrors[field] = `${label} must be a number`;
        return;
      }
      if (value && rules.custom) {
        const customError = rules.custom(value);
        if (customError) newErrors[field] = customError;
      }
    });

    if (qrCodeFile && !qrCodeFile.type?.startsWith("image/")) {
      newErrors.qrCodeFile = "Only image files are allowed.";
    }
    if (!logo) {
      newErrors.logo = "Please upload a logo";
    }

    return newErrors;
  };

  // --- Submit ---
  const handleSubmit = async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = new FormData();
      payload.append("sub_name", formData.NameofCompany);
      payload.append("address", formData.Address);
      payload.append("owner", formData.NameofOwner);
      payload.append("contact_no", formData.MobileNo);
      payload.append("email_id", formData.Email);
      payload.append("hasGST", isGstYes ? "Yes" : "No");
      payload.append("gst_no", isGstYes ? formData.GstNO : "");
      payload.append("invoicing_limit", formData.InvoicingLimit);
      payload.append("tag_name", formData.TagName);
      payload.append("account_number", formData.AccountNumber);
      payload.append("account_holder", formData.AccountHolder);
      payload.append("ifsc_code", formData.IFSCCode);
      payload.append("branch", formData.Branch);
      payload.append("pan_no", formData.PanNumber);
      payload.append("vpa", formData.VirtualPayementAddress);
      if (logo) payload.append("logo", logo);
      if (qrCodeFile) payload.append("qr_code", qrCodeFile);

      const response = await axiosInstance.post("/api/create_subsidiary", payload);

      if (response.status === 200) {
        const refetch = await axiosInstance.get("/api/get_all_subsidiary?page_status=Active");
  const firms = refetch.data.data || [];
  const updatedList = firms.map((company) => ({
    label: company.Subsidiary_name,
    value: company.subsidiary_id,
    tag_name: company.tag_name,
  }));

  // Find the newly created one by name to auto-select it
  const created = updatedList.find(
    (sub) => sub.label === formData.NameofCompany.trim()
  );
        
        // Pass new subsidiary back to parent to auto-select it
        onSuccess(created, response.data?.message || "Subsidiary created successfully");
        resetForm();
        onClose();
      }
    } catch (error) {
        
      const message =
        error.response?.status === 400
          ? error.response?.data?.error ||
            "Maximum subsidiaries limit reached."
          : error.response?.data?.message || error.message;
          setSnackbarMessage(message);
  setSnackbarOpen(true);
      
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="subsidiary-modal-root">
      <div className="subsidiary-modal-overlay" onClick={handleClose} />
      <div className="subsidiary-modal-wrapper">
        <div className="subsidiary-modal-header">
          <div>
            <h2 className="subsidiary-modal-title">Create New Subsidiary</h2>
            <p className="subsidiary-modal-subtitle">
              Add company, tax, payment and branding details.
            </p>
          </div>
          <button
            type="button"
            className="subsidiary-modal-close"
            onClick={handleClose}
            aria-label="Close subsidiary modal"
          >
            <FiX />
          </button>
        </div>

        <div className="subsidiary-modal-body">
          {errors._submit && (
            <p className="subsidiary-error-submit">{errors._submit}</p>
          )}

          <div className="subsidiary-modal-sections">
            <section className="subsidiary-modal-section">
              <div className="subsidiary-section-header">
                <h3 className="subsidiary-section-title">Company Details</h3>
                <p className="subsidiary-section-description">
                  Add the primary business and contact details for the
                  subsidiary.
                </p>
              </div>
              <div className="subsidiary-modal-grid">
                <div className="subsidiary-field">
                  <label className="subsidiary-field-label">
                    Company Name <span className="subsidiary-required">*</span>
                  </label>
                  <input
                    ref={firstInputRef}
                    className="subsidiary-input"
                    placeholder="Company Name *"
                    name="NameofCompany"
                    value={formData.NameofCompany}
                    maxLength={100}
                    onChange={handleInputChange}
                  />
                  {errors.NameofCompany && (
                    <p className="subsidiary-error">{errors.NameofCompany}</p>
                  )}
                </div>
                <div className="subsidiary-field">
                  <label className="subsidiary-field-label">
                    Proprietor / Owner Name{" "}
                    <span className="subsidiary-required">*</span>
                  </label>
                  <input
                    className="subsidiary-input"
                    placeholder="Proprietor / Owner Name *"
                    name="NameofOwner"
                    value={formData.NameofOwner}
                    maxLength={50}
                    onChange={handleInputChange}
                  />
                  {errors.NameofOwner && (
                    <p className="subsidiary-error">{errors.NameofOwner}</p>
                  )}
                </div>
                <div className="subsidiary-field">
                  <label className="subsidiary-field-label">
                    Mobile Number <span className="subsidiary-required">*</span>
                  </label>
                  <input
                    className="subsidiary-input"
                    placeholder="Mobile Number *"
                    value={formData.MobileNo}
                    maxLength={10}
                    inputMode="numeric"
                    onChange={(e) => handleMobileChange(e.target.value)}
                  />
                  {errors.MobileNo && (
                    <p className="subsidiary-error">{errors.MobileNo}</p>
                  )}
                </div>
                <div className="subsidiary-field">
                  <label className="subsidiary-field-label">
                    Email <span className="subsidiary-required">*</span>
                  </label>
                  <input
                    className="subsidiary-input"
                    placeholder="Email *"
                    name="Email"
                    value={formData.Email}
                    onChange={handleInputChange}
                  />
                  {errors.Email && (
                    <p className="subsidiary-error">{errors.Email}</p>
                  )}
                </div>
                <div className="subsidiary-field subsidiary-field--full">
                  <label className="subsidiary-field-label">
                    Address <span className="subsidiary-required">*</span>
                  </label>
                  <input
                    className="subsidiary-input"
                    placeholder="Address *"
                    name="Address"
                    value={formData.Address}
                    maxLength={250}
                    onChange={handleInputChange}
                  />
                  {errors.Address && (
                    <p className="subsidiary-error">{errors.Address}</p>
                  )}
                </div>
                <div className="subsidiary-field">
                  <label className="subsidiary-field-label">
                    Tag Name <span className="subsidiary-required">*</span>
                  </label>
                  <input
                    className="subsidiary-input"
                    placeholder="Tag Name *"
                    name="TagName"
                    value={formData.TagName}
                    maxLength={50}
                    onChange={handleInputChange}
                  />
                  {errors.TagName && (
                    <p className="subsidiary-error">{errors.TagName}</p>
                  )}
                </div>
              </div>
            </section>

            <section className="subsidiary-modal-section">
              <div className="subsidiary-section-header">
                <h3 className="subsidiary-section-title">
                  Billing &amp; Tax Details
                </h3>
                <p className="subsidiary-section-description">
                  Configure invoicing, PAN and GST details for this subsidiary.
                </p>
              </div>
              <div className="subsidiary-modal-grid">
                <div className="subsidiary-field">
                  <label className="subsidiary-field-label">
                    Invoicing Limit{" "}
                    <span className="subsidiary-required">*</span>
                  </label>
                  <input
                    className="subsidiary-input"
                    placeholder="Invoicing Limit *"
                    name="InvoicingLimit"
                    value={formData.InvoicingLimit}
                    onChange={handleInputChange}
                  />
                  {errors.InvoicingLimit && (
                    <p className="subsidiary-error">{errors.InvoicingLimit}</p>
                  )}
                </div>
                <div className="subsidiary-field">
                  <label className="subsidiary-field-label">
                    PAN Number <span className="subsidiary-required">*</span>
                  </label>
                  <input
                    className="subsidiary-input"
                    placeholder="PAN Number *"
                    name="PanNumber"
                    value={formData.PanNumber}
                    maxLength={10}
                    onChange={handleInputChange}
                  />
                  {errors.PanNumber && (
                    <p className="subsidiary-error">{errors.PanNumber}</p>
                  )}
                </div>
                <div className="subsidiary-field subsidiary-field--full">
                  <label className="subsidiary-field-label">GST:</label>
                  <div className="subsidiary-gst-row">
                    <label className="subsidiary-radio-label">
                      <input
                        type="radio"
                        value="yes"
                        checked={isGstYes}
                        onChange={handleGstChange}
                      />{" "}
                      Yes
                    </label>
                    <label className="subsidiary-radio-label">
                      <input
                        type="radio"
                        value="no"
                        checked={!isGstYes}
                        onChange={handleGstChange}
                      />{" "}
                      No
                    </label>
                  </div>
                </div>
                {isGstYes && (
                  <div className="subsidiary-field subsidiary-field--full">
                    <label className="subsidiary-field-label">
                      GST Number <span className="subsidiary-required">*</span>
                    </label>
                    <input
                      className="subsidiary-input"
                      placeholder="GST Number *"
                      name="GstNO"
                      value={formData.GstNO}
                      onChange={handleInputChange}
                    />
                    {errors.GstNO && (
                      <p className="subsidiary-error">{errors.GstNO}</p>
                    )}
                  </div>
                )}
              </div>
            </section>

            <section className="subsidiary-modal-section">
              <div className="subsidiary-section-header">
                <h3 className="subsidiary-section-title">
                  Bank &amp; Payment Details
                </h3>
                <p className="subsidiary-section-description">
                  Add the bank and payment information used for collections.
                </p>
              </div>
              <div className="subsidiary-modal-grid">
                <div className="subsidiary-field">
                  <label className="subsidiary-field-label">
                    Account Number{" "}
                    <span className="subsidiary-required">*</span>
                  </label>
                  <input
                    className="subsidiary-input"
                    placeholder="Account Number *"
                    name="AccountNumber"
                    value={formData.AccountNumber}
                    onChange={handleInputChange}
                  />
                  {errors.AccountNumber && (
                    <p className="subsidiary-error">{errors.AccountNumber}</p>
                  )}
                </div>
                <div className="subsidiary-field">
                  <label className="subsidiary-field-label">
                    Account Holder{" "}
                    <span className="subsidiary-required">*</span>
                  </label>
                  <input
                    className="subsidiary-input"
                    placeholder="Account Holder *"
                    name="AccountHolder"
                    value={formData.AccountHolder}
                    onChange={handleInputChange}
                  />
                  {errors.AccountHolder && (
                    <p className="subsidiary-error">{errors.AccountHolder}</p>
                  )}
                </div>
                <div className="subsidiary-field">
                  <label className="subsidiary-field-label">
                    IFSC Code <span className="subsidiary-required">*</span>
                  </label>
                  <input
                    className="subsidiary-input"
                    placeholder="IFSC Code *"
                    name="IFSCCode"
                    value={formData.IFSCCode}
                    onChange={handleInputChange}
                  />
                  {errors.IFSCCode && (
                    <p className="subsidiary-error">{errors.IFSCCode}</p>
                  )}
                </div>
                <div className="subsidiary-field">
                  <label className="subsidiary-field-label">
                    Bank Branch (optional)
                  </label>
                  <input
                    className="subsidiary-input"
                    placeholder="Bank Branch (optional)"
                    name="Branch"
                    value={formData.Branch}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="subsidiary-field">
                  <label className="subsidiary-field-label">
                    UPI ID <span className="subsidiary-required">*</span>
                  </label>
                  <input
                    className="subsidiary-input"
                    placeholder="UPI ID *"
                    name="VirtualPayementAddress"
                    value={formData.VirtualPayementAddress}
                    maxLength={100}
                    onChange={handleInputChange}
                  />
                  {errors.VirtualPayementAddress && (
                    <p className="subsidiary-error">
                      {errors.VirtualPayementAddress}
                    </p>
                  )}
                </div>
              </div>
            </section>

            <section className="subsidiary-modal-section subsidiary-modal-section-last">
              <div className="subsidiary-section-header">
                <h3 className="subsidiary-section-title">
                  Branding &amp; Payment Uploads
                </h3>
                <p className="subsidiary-section-description">
                  Upload QR and logo assets used in the subsidiary profile.
                </p>
              </div>
              <div className="subsidiary-upload-grid">
                <div className="subsidiary-upload-block">
                  <input
                    ref={paymentQrInputRef}
                    type="file"
                    id="sub_modal_qr"
                    accept="image/*"
                    onChange={handlePaymentQrChange}
                    style={{ display: "none" }}
                  />
                  <label
                    htmlFor="sub_modal_qr"
                    className="subsidiary-upload-tile"
                  >
                    <span className="subsidiary-upload-icon" aria-hidden="true">
                      <FiUpload />
                    </span>
                    <span className="subsidiary-upload-copy">
                      <span className="subsidiary-upload-title">Upload QR</span>
                      <span className="subsidiary-upload-meta">Optional</span>
                    </span>
                    <span className="subsidiary-upload-action">
                      {qrCodeFile ? "Change QR" : "Choose File"}
                    </span>
                  </label>
                  {errors.qrCodeFile && (
                    <p className="subsidiary-error">{errors.qrCodeFile}</p>
                  )}
                  {qrCodeFile && (
                    <div className="subsidiary-upload-selected">
                      <span>{qrCodeFile.name}</span>
                      <button
                        type="button"
                        className="subsidiary-remove-btn"
                        onClick={handleClearQr}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>

                <div className="subsidiary-upload-block">
                  <input
                    accept="image/*"
                    style={{ display: "none" }}
                    id="sub_modal_logo"
                    type="file"
                    onChange={handleFileChange}
                  />
                  <label
                    htmlFor="sub_modal_logo"
                    className="subsidiary-upload-tile"
                  >
                    <span className="subsidiary-upload-icon" aria-hidden="true">
                      <FiImage />
                    </span>
                    <span className="subsidiary-upload-copy">
                      <span className="subsidiary-upload-title">
                        Upload Logo
                      </span>
                      <span className="subsidiary-upload-meta">Required</span>
                    </span>
                    <span className="subsidiary-upload-action">
                      {logo ? "Change Logo" : "Choose File"}
                    </span>
                  </label>
                  {errors.logo && (
                    <p className="subsidiary-error">{errors.logo}</p>
                  )}
                  {logoPreview && (
                    <div className="subsidiary-logo-preview">
                      <img src={logoPreview} alt="Logo Preview" />
                    </div>
                  )}
                </div>
              </div>
            </section>
          </div>
        </div>

        <div className="subsidiary-modal-buttons">
          <button
            type="button"
            className="btn btn-cancel"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-save"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Creating..." : "Create"}
          </button>
        </div>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          sx={{ zIndex: 10001 }}
        >
          <MuiAlert
            onClose={() => setSnackbarOpen(false)}
            severity="error"
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </MuiAlert>
        </Snackbar>
      </div>
    </div>,
  document.body
);
}
